import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Check, Settings2 } from 'lucide-react';
import type { NodeUpdateEntry } from './types';

export interface NodeUpdateMessageProps {
  updates: NodeUpdateEntry[];
  applied: boolean;
  onApply: () => void;
  // When true, the workflow is locked and the Apply button is disabled
  // with an explanatory tooltip. This is the final UI safeguard — the
  // backend already hides mutation tools and refuses any hallucinated
  // calls, but a proposal card from BEFORE the lock could still be in
  // the chat history. Disabling Apply prevents it from being applied.
  applyDisabled?: boolean;
  applyDisabledReason?: string;
}

const PREVIEW_LENGTH = 120;

function formatFieldName(fieldId: string): string {
  return fieldId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const UpdateEntry: React.FC<{ entry: NodeUpdateEntry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const entryRef = useRef<HTMLLIElement>(null);
  const isLong = entry.value.length > PREVIEW_LENGTH;

  useEffect(() => {
    if (open && entryRef.current) {
      setTimeout(() => {
        entryRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    }
  }, [open]);

  return (
    <li ref={entryRef} className='text-xs'>
      <span className='font-medium text-zinc-700 dark:text-zinc-300'>
        {formatFieldName(entry.field_id)}
      </span>
      <span className='text-zinc-400 dark:text-zinc-500'>{' → '}</span>
      {isLong ? (
        <>
          <span className='whitespace-pre-wrap break-words text-zinc-600 dark:text-zinc-400'>
            {open ? entry.value : entry.value.slice(0, PREVIEW_LENGTH) + '…'}
          </span>
          <button
            type='button'
            onClick={() => setOpen((v) => !v)}
            className='ml-1 text-primary hover:underline'
          >
            {open ? 'Show less' : 'Show more'}
          </button>
        </>
      ) : (
        <span className='whitespace-pre-wrap break-words text-zinc-600 dark:text-zinc-400'>
          {entry.value}
        </span>
      )}
    </li>
  );
};

export const NodeUpdateMessage: React.FC<NodeUpdateMessageProps> = ({
  updates,
  applied,
  onApply,
  applyDisabled = false,
  applyDisabledReason,
}) => {
  const [expanded, setExpanded] = useState(false);
  const detailListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded && detailListRef.current) {
      setTimeout(() => {
        detailListRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    }
  }, [expanded]);

  const count = updates.length;
  const summary = `${count} config change${count !== 1 ? 's' : ''}`;

  return (
    <div className='rounded-xl border border-border bg-zinc-50 dark:bg-zinc-900'>
      {/* Header row: toggle + summary + apply */}
      <div className='flex items-center gap-2 px-3 py-2'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors'
        >
          {expanded ? (
            <ChevronDown className='size-3.5' />
          ) : (
            <ChevronRight className='size-3.5' />
          )}
          <Settings2 className='size-3.5' />
          <span>{summary}</span>
        </button>

        <div className='flex-1' />

        {applied ? (
          <span className='flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400'>
            <Check className='size-3.5' />
            Applied
          </span>
        ) : (
          <button
            type='button'
            onClick={onApply}
            disabled={applyDisabled}
            title={applyDisabled ? applyDisabledReason : undefined}
            className='rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-primary'
          >
            Apply changes
          </button>
        )}
      </div>

      {/* Expanded detail list */}
      {expanded && (
        <div
          ref={detailListRef}
          className='border-t border-border px-3 pb-2 pt-1.5'
        >
          <ul className='space-y-1.5'>
            {updates.map((u) => (
              <UpdateEntry key={u.field_id} entry={u} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
